import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { getNewsletter, deleteNewsletter } from '../services/newsletters';
import { useToast } from '../hooks/useToast';
import PageLoading from '../components/PageLoading';
import { formatRelativeDate } from '../utils/dateUtils';

const POLL_INTERVAL_MS = 2000;
const IN_FLIGHT_STATUSES = new Set(['pending', 'generating']);

const formatWindow = (start, end) => {
  const fmt = (s) => new Date(s).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  return `${fmt(start)} – ${fmt(end)}`;
};

const NewsletterDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();

  const [newsletter, setNewsletter] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const timerRef = useRef(null);

  const load = async () => {
    try {
      const data = await getNewsletter(id);
      setNewsletter(data);
      setError(null);
      return data;
    } catch (err) {
      if (err.response?.status === 404) {
        setError('Newsletter not found.');
      } else {
        setError(err.response?.data?.detail || err.message || 'Failed to load newsletter');
      }
      return null;
    }
  };

  useEffect(() => {
    let cancelled = false;

    const poll = async () => {
      const data = await load();
      if (cancelled) return;

      setIsLoading(false);

      if (data && IN_FLIGHT_STATUSES.has(data.status)) {
        timerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
      }
    };

    poll();

    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [id]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(newsletter.markdown || '');
      showSuccess('Copied to clipboard');
    } catch {
      showError('Failed to copy');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this newsletter?')) return;
    setIsDeleting(true);
    try {
      await deleteNewsletter(id);
      navigate('/newsletters');
    } catch (err) {
      showError(err.response?.data?.detail || err.message || 'Failed to delete');
      setIsDeleting(false);
    }
  };

  if (isLoading) return <PageLoading message="Loading newsletter…" />;

  if (error) {
    return (
      <div className="min-h-screen bg-clay-bg flex items-center justify-center">
        <div className="card-clay text-center p-8 max-w-md">
          <p className="text-rose-600 mb-4">{error}</p>
          <Link to="/newsletters" className="btn-clay-secondary text-sm">← Back to newsletters</Link>
        </div>
      </div>
    );
  }

  const isInFlight = IN_FLIGHT_STATUSES.has(newsletter.status);

  return (
    <div className="min-h-screen bg-clay-bg">
      {/* Header */}
      <div className="bg-surface border-b-3 border-clay-border shadow-clay-sm">
        <div className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8 py-6">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-start min-w-0">
              <Link to="/newsletters" className="btn-clay-ghost mr-4 p-2 flex-shrink-0" aria-label="Back to newsletters">
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </Link>
              <div className="min-w-0">
                <h1 className="font-display text-2xl font-bold text-heading flex items-center gap-2">
                  <span role="img" aria-hidden="true">📰</span>
                  <span className="truncate">{newsletter.theme_name}</span>
                </h1>
                <p className="text-sm text-secondary-text mt-1">
                  {formatWindow(newsletter.window_start, newsletter.window_end)}
                  <span className="mx-2">·</span>
                  {newsletter.language.toUpperCase()}
                  <span className="mx-2">·</span>
                  {formatRelativeDate(newsletter.created_at)}
                </p>
              </div>
            </div>

            {newsletter.status === 'completed' && (
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  onClick={() => navigate('/newsletters/new', {
                    state: {
                      prefill: {
                        theme_name: newsletter.theme_name,
                        theme_intent: newsletter.theme_intent,
                        language: newsletter.language,
                        window_start: newsletter.window_start,
                        window_end: newsletter.window_end,
                      },
                    },
                  })}
                  className="btn-clay text-sm"
                >
                  Regenerate
                </button>
                <button onClick={handleCopy} className="btn-clay text-sm">
                  Copy Markdown
                </button>
                <button
                  onClick={handleDelete}
                  disabled={isDeleting}
                  className="btn-clay-ghost text-sm text-rose-600 hover:text-rose-700"
                >
                  {isDeleting ? 'Deleting…' : 'Delete'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* In-flight status */}
        {isInFlight && (
          <div className="card-clay p-6 text-center">
            <div className="spinner-fun-lg mx-auto mb-3"></div>
            <p className="text-secondary-text capitalize">{newsletter.status}…</p>
          </div>
        )}

        {/* Failed status */}
        {newsletter.status === 'failed' && (
          <div className="card-clay border-rose-300 bg-rose-50 dark:bg-rose-900/20 p-6">
            <p className="font-semibold text-rose-700 dark:text-rose-300 mb-1">Generation failed</p>
            {newsletter.failure_message && (
              <p className="text-sm text-rose-600 dark:text-rose-400">{newsletter.failure_message}</p>
            )}
            <div className="flex items-center gap-2 mt-4">
              <button
                onClick={() => navigate('/newsletters/new', {
                  state: {
                    prefill: {
                      theme_name: newsletter.theme_name,
                      theme_intent: newsletter.theme_intent,
                      language: newsletter.language,
                      window_start: newsletter.window_start,
                      window_end: newsletter.window_end,
                    },
                  },
                })}
                className="btn-clay text-sm"
              >
                Regenerate
              </button>
              <button
                onClick={handleDelete}
                disabled={isDeleting}
                className="btn-clay-ghost text-sm text-rose-600 hover:text-rose-700"
              >
                {isDeleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        )}

        {/* Completed: markdown body */}
        {newsletter.status === 'completed' && newsletter.markdown && (
          <div className="card-clay p-6 mb-4">
            <div className="prose prose-sm max-w-none dark:prose-invert">
              <ReactMarkdown>{newsletter.markdown}</ReactMarkdown>
            </div>
          </div>
        )}

        {/* Verification summary */}
        {newsletter.status === 'completed' && (
          <details className="card-clay p-4 text-sm text-secondary-text">
            <summary className="cursor-pointer font-medium text-body-text">Verification summary</summary>
            <p className="mt-2">
              {newsletter.removed_citations
                ? `${newsletter.removed_citations} citation${newsletter.removed_citations === 1 ? '' : 's'} removed during verification.`
                : 'All citations verified successfully.'}
            </p>
          </details>
        )}
      </div>
    </div>
  );
};

export default NewsletterDetail;
